// src/i18n/useT.ts
//
// Runtime translation hook. Today this returns the English source
// verbatim (with interpolation support). Drop 3 will hydrate the
// dictionary from Firestore; the call site `t("Send message")`
// doesn't change.
//
// Usage:
//
//     const t = useT();
//     return <Button>{t("Send message")}</Button>;
//
// With interpolation:
//
//     <Typography>{t("Hi {name}, you have {n} messages",
//                   { name: user.name, n: count })}</Typography>
//
// The first argument is BOTH the key and the English fallback.
// Editing the English string changes its hash → the build
// extractor sees a missing entry → translators (LLM in Drop 2 or
// real humans later) re-translate exactly that one entry.
//
// IMPORTANT: keep the first argument a STATIC string literal
// (not a variable, not a template-literal with `${...}`). The
// static-analysis extractor and the ESLint rule both rely on
// being able to read the source at compile time. For dynamic
// content, use interpolation (the second argument) instead of
// concatenation.

import { useEffect, useMemo, useState } from 'react';
import { useLanguagePreference } from './useLanguagePreference';
import {
  hashSource,
  normalizeSource,
  StringsDict,
} from './i18nConfig';

/** Variables interpolated into the translated string. Values are
 *  coerced to strings via String(). Numbers, booleans, and dates
 *  all work — for richer formatting (currencies, plurals)
 *  format in the call site or use a dedicated formatter. */
export type TranslateVars = Record<string, string | number | boolean | null | undefined>;

/** The translate function returned by useT(). */
export type TranslateFn = (source: string, vars?: TranslateVars) => string;

/** Module-level dictionary cache. Single instance shared by all
 *  useT() consumers in the process. Drop 3 will populate this
 *  from Firestore + bundled fallback after the user picks a
 *  language. Today it stays empty so every call falls through to
 *  the English source — which IS what we want for Drop 1.
 *
 *  The cache is intentionally module-scoped (not React state):
 *  the dictionary changes very rarely (only on language switch)
 *  and a re-render storm on every t() call would be expensive.
 *  Subscribers are notified via the `subscribers` set below. */
let dictionary: StringsDict = { version: 1, entries: {} };

/** Notify-on-change subscribers so components re-render when the
 *  dictionary changes (Drop 3 use). Stored as a Set of plain
 *  callbacks rather than a context to avoid forcing every t()
 *  consumer to be inside a Provider. */
const subscribers = new Set<() => void>();

function notify() {
  // Defer to a microtask. Without this, an installDictionary call
  // that lands during a render (e.g. a Firestore snapshot listener
  // arriving while the standby screen is mid-render) synchronously
  // setStates inside subscribers and React logs:
  //
  //   "Cannot update a component while rendering a different
  //    component."
  //
  // queueMicrotask runs after the current render commit but before
  // the browser paints, so subscribers re-render with the fresh
  // dictionary on the very next tick — no visible delay, no
  // warning. Falls back to setTimeout(0) for environments without
  // queueMicrotask (very old; defensive).
  const flush = () => {
    subscribers.forEach((cb) => {
      try {
        cb();
      } catch {
        /* ignore subscriber errors */
      }
    });
  };
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(flush);
  } else {
    setTimeout(flush, 0);
  }
}

/** Drop-3 entry point. Today nothing calls this; it's exported
 *  so the future Firestore loader has a clean install seam. */
export function installDictionary(next: StringsDict): void {
  // Quick equality check — if the incoming dictionary has the
  // same entries (count + values) as the current one, this is a
  // no-op snapshot. Calling notify() anyway would force every
  // useT() subscriber to bump its tick and re-render, which on a
  // complex screen looks visually like a "refresh" even though
  // nothing changed. Translation progress writes can fire several
  // times per second during generation; without this guard the
  // app re-renders every consumer ~10x for the same content.
  const currentEntries = dictionary.entries || {};
  const nextEntries = next.entries || {};
  const currentKeys = Object.keys(currentEntries);
  const nextKeys = Object.keys(nextEntries);
  let changed = currentKeys.length !== nextKeys.length;
  if (!changed) {
    for (const k of nextKeys) {
      if (currentEntries[k] !== nextEntries[k]) {
        changed = true;
        break;
      }
    }
  }
  dictionary = next;
  if (changed) notify();
}

/** Drop-3 entry point. Inspect what's currently loaded — useful
 *  for the standby screen progress count. */
export function getDictionary(): StringsDict {
  return dictionary;
}

/* ───────────────────────── The hook ───────────────────────── */

export function useT(): TranslateFn {
  // Subscribe to language changes so a flag-icon swap triggers a
  // re-render in components consuming t(). Today the only thing
  // that affects the returned function's BEHAVIOR is the
  // dictionary contents, but plumbing the language through means
  // Drop 3's dictionary swap will Just Work.
  const { englishName } = useLanguagePreference();

  // Force-update tick; bumped when the dictionary install
  // notifies. The closure below re-reads the live `dictionary`
  // module variable each call, so the tick exists only to
  // trigger React's render.
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    subscribers.add(cb);
    return () => { subscribers.delete(cb); };
  }, []);

  // The actual translator. Stable per-language so component memoization
  // downstream doesn't get blown out on every render.
  return useMemo<TranslateFn>(() => {
    return (source, vars) => translate(source, vars);
  }, [englishName]);
}

/** Standalone translate — for non-component contexts (rare, but
 *  e.g. zustand store messages, error throwers). No subscription;
 *  whatever dictionary is current at call time is used. */
export function translate(source: string, vars?: TranslateVars): string {
  const key = hashSource(source);
  const entry = dictionary.entries[key];
  // Fall back to source on miss. This is the safety net: a
  // freshly-shipped string that hasn't been translated yet
  // renders in English instead of crashing or showing the hash.
  const template = entry ?? normalizeSource(source);
  if (!vars) return template;
  return interpolate(template, vars);
}

/** Replace `{name}` tokens with values from `vars`. Unmatched
 *  tokens are left as-is — a typo in a var name shows up
 *  visibly rather than silently disappearing. */
function interpolate(template: string, vars: TranslateVars): string {
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    if (Object.prototype.hasOwnProperty.call(vars, name)) {
      const v = vars[name];
      return v == null ? '' : String(v);
    }
    return match;
  });
}
